/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useFavorites } from '../../contexts/UserContext';
import Button from '../buttons/Button';
import FavoriteButton from '../buttons/Favorite';
import StyledButton from '../buttons/StyledButton';
import Image from '../partials/Image';
import Text from '../typography/Text';
import Title from '../typography/Title';

import styles from '../../../styles/cards/film.module.css';

const propTypes = {
    item: PropTypes.shape({}),
    wide: PropTypes.bool,
    withFavorite: PropTypes.bool,
    withContents: PropTypes.bool,
    className: PropTypes.string,
};

function FilmCard({
    item = null,
    wide = null,
    withFavorite = false,
    withContents = false,
    className = null,
    ...props
}) {
    const {
        contents = null,
        mediaUrl = null,
        title = null,
        year = null,
        duration = null,
        location = null,
        description = null,
        image = null,
        url = null,
        credits = null,
        id = null,
    } = item || {};

    const directors = useMemo(
        () =>
            (credits || [])
                .filter(
                    ({ roles = [] }) =>
                        (roles || []).map((role) => role.handle).indexOf('director') !== -1,
                )
                .map(({ person = null }) => person?.name)
                .join(', ') || null,
        [credits],
    );

    const { hasFavorite, onChangeFavorite } = useFavorites();
    const isFavorite = hasFavorite(id);
    const onClickFavorite = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            onChangeFavorite(id);
        },
        [id, onChangeFavorite],
    );

    return (
        <Button
            className={classNames([
                styles.container,
                {
                    [styles.wide]: wide !== null,
                    [className]: className !== null,
                },
            ])}
            href={url}
            {...props}
        >
            <div className={styles.top}>
                <Image
                    className={styles.image}
                    image={image}
                    size="medium"
                    filtered
                    filteredHover={url !== null}
                    rounded
                />
                {withFavorite ? (
                    <FavoriteButton
                        className={classNames([styles.star, { [styles.active]: isFavorite }])}
                        onClick={onClickFavorite}
                    />
                ) : null}
            </div>
            {wide ? (
                <div className={styles.content}>
                    <Title className={styles.titleSmall} size="small">
                        <FormattedMessage
                            defaultMessage="{duration} min"
                            description="Film label"
                            values={{ duration }}
                        />
                        {location !== null ? (
                            <>
                                {' — '}
                                {location}
                            </>
                        ) : null}
                        {year !== null ? (
                            <>
                                {' — '}
                                {year}
                            </>
                        ) : null}
                    </Title>
                    <Title className={styles.titleLarge} size="mediumLarge">
                        {title}
                    </Title>
                    <Title className={styles.titleMedium}>{directors}</Title>
                    <Text className={styles.text}>{description}</Text>
                </div>
            ) : (
                <div className={styles.content}>
                    <Title className={styles.title} size="small">
                        {title}
                    </Title>
                    <Text className={styles.description}>
                        {directors !== null ? (
                            <>
                                {directors}
                                {' — '}
                            </>
                        ) : null}
                        {year}
                        {' — '}
                        <FormattedMessage
                            defaultMessage="{duration} min"
                            description="Film label"
                            values={{ duration }}
                        />
                    </Text>
                </div>
            )}
            {withContents ? (
                <div className={styles.contents}>
                    {mediaUrl !== null ? (
                        <StyledButton className={styles.downloadButton} href={mediaUrl} external>
                            <FormattedMessage
                                defaultMessage="Lien de téléchargement du film"
                                description="Film label"
                            />
                        </StyledButton>
                    ) : null}
                    <div className={styles.items}>
                        {contents !== null && contents.length > 0
                            ? (contents || []).map((content) => {
                                  const {
                                      label = null,
                                      url: contentUrl = null,
                                      document = null,
                                  } = content || {};
                                  const { url: documentUrl = null } = document || {};
                                  return (
                                      <StyledButton
                                          className={styles.contentButton}
                                          size="medium"
                                          href={documentUrl || contentUrl}
                                          download
                                          external
                                      >
                                          {label}
                                      </StyledButton>
                                  );
                              })
                            : null}
                    </div>
                </div>
            ) : null}
        </Button>
    );
}

FilmCard.propTypes = propTypes;

export default FilmCard;
